import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  BookingFormData,
  BookingStep,
  Slot,
  SlotsStatus,
  SubmitStatus,
} from "../types/booking";
import { createBooking, getDaySlots } from "../services/api";
import { validateEmail } from "../lib/validateEmail";
import { toHalifaxDateString, toHalifaxWallClock } from "../lib/timezone";

interface UseBookingFormProps {
  selectedPackageId: string;
}

interface BookingState {
  step: BookingStep;
  formData: BookingFormData;
  errors: Partial<Record<keyof BookingFormData, boolean>>;
  selectedDate: string;
  slots: Slot[];
  slotsStatus: SlotsStatus;
  selectedSlot: Slot | null;
  submitStatus: SubmitStatus;
  submitError: string | null;
  showSuccess: boolean;
  confirmation: { slot: Slot; name: string } | null;
}

type Action =
  | { type: "SET_FIELDS"; values: Partial<BookingFormData> }
  | { type: "SET_ERRORS"; errors: Partial<Record<keyof BookingFormData, boolean>> }
  | { type: "SET_STEP"; step: BookingStep }
  | { type: "SELECT_DATE"; date: string }
  | { type: "SLOTS_LOADING" }
  | { type: "SLOTS_LOADED"; slots: Slot[] }
  | { type: "SLOTS_ERROR" }
  | { type: "SELECT_SLOT"; slot: Slot }
  | { type: "SUBMIT_START" }
  | { type: "SUBMIT_ERROR"; message: string }
  | { type: "SLOT_TAKEN"; slots: Slot[] }
  | { type: "SUBMIT_SUCCESS"; confirmation: { slot: Slot; name: string } };

const initialState: BookingState = {
  step: "location",
  formData: {} as BookingFormData,
  errors: {},
  selectedDate: "",
  slots: [],
  slotsStatus: "idle",
  selectedSlot: null,
  submitStatus: "idle",
  submitError: null,
  showSuccess: false,
  confirmation: null,
};

function reducer(state: BookingState, action: Action): BookingState {
  switch (action.type) {
    case "SET_FIELDS": {
      const errors = { ...state.errors };
      (Object.keys(action.values) as (keyof BookingFormData)[]).forEach(
        (key) => delete errors[key]
      );
      return { ...state, formData: { ...state.formData, ...action.values }, errors };
    }
    case "SET_ERRORS":
      return { ...state, errors: action.errors };
    case "SET_STEP":
      return { ...state, step: action.step };
    case "SELECT_DATE":
      // Changing the date invalidates any previously chosen slot.
      return {
        ...state,
        selectedDate: action.date,
        slots: [],
        slotsStatus: "loading",
        selectedSlot: null,
        errors: { ...state.errors, date: false, time: false },
      };
    case "SLOTS_LOADING":
      return { ...state, slotsStatus: "loading" };
    case "SLOTS_LOADED":
      return { ...state, slots: action.slots, slotsStatus: "loaded" };
    case "SLOTS_ERROR":
      return { ...state, slotsStatus: "error" };
    case "SELECT_SLOT":
      return {
        ...state,
        selectedSlot: action.slot,
        errors: { ...state.errors, time: false },
      };
    case "SUBMIT_START":
      return { ...state, submitStatus: "submitting", submitError: null };
    case "SUBMIT_ERROR":
      return { ...state, submitStatus: "error", submitError: action.message };
    case "SLOT_TAKEN":
      // The slot was taken between selection and confirmation. Drop it, refresh
      // the list, and surface a clear recovery path — never a dead end.
      return {
        ...state,
        submitStatus: "idle",
        submitError: null,
        selectedSlot: null,
        slots: action.slots,
        slotsStatus: "loaded",
      };
    case "SUBMIT_SUCCESS":
      return {
        ...state,
        submitStatus: "idle",
        showSuccess: true,
        confirmation: action.confirmation,
      };
    default:
      return state;
  }
}

function facesCountOf(formData: BookingFormData, customFacesCount: string): number {
  if (formData.numberOfFaces === "more") {
    return parseInt(customFacesCount || formData.customFacesCount || "1", 10) || 1;
  }
  return parseInt(formData.numberOfFaces || "1", 10) || 1;
}

export function useBookingForm({ selectedPackageId }: UseBookingFormProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [image, setImage] = useState<File | null>(null);
  const [customFacesCount, setCustomFacesCount] = useState("");
  const [featuredImageConsent, setFeaturedImageConsent] = useState<boolean | null>(
    null
  );

  // Refs keep the latest values available to async callbacks without stale closures.
  const abortRef = useRef<AbortController | null>(null);
  const reqIdRef = useRef(0);
  const facesRef = useRef(1);
  facesRef.current = facesCountOf(state.formData, customFacesCount);

  useEffect(() => () => abortRef.current?.abort(), []);

  const updateFormData = useCallback((values: Partial<BookingFormData>) => {
    dispatch({ type: "SET_FIELDS", values });
  }, []);

  const handleConsentChange = useCallback((consent: boolean) => {
    setFeaturedImageConsent(consent);
    dispatch({ type: "SET_FIELDS", values: { featured_image_discount: consent } });
  }, []);

  const fetchSlots = useCallback(
    async (date: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const reqId = ++reqIdRef.current;

      try {
        const response: any = await getDaySlots(
          {
            date,
            glam_package_id: selectedPackageId,
            number_of_faces: facesRef.current,
          },
          { signal: controller.signal }
        );
        if (reqId !== reqIdRef.current) return; // a newer request superseded this one
        dispatch({ type: "SLOTS_LOADED", slots: response.data?.data?.slots ?? [] });
      } catch (error: any) {
        if (controller.signal.aborted || reqId !== reqIdRef.current) return;
        dispatch({ type: "SLOTS_ERROR" });
      }
    },
    [selectedPackageId]
  );

  const selectDate = useCallback(
    (date: string) => {
      if (!date) return;
      dispatch({ type: "SELECT_DATE", date });
      fetchSlots(date);
    },
    [fetchSlots]
  );

  const retrySlots = useCallback(() => {
    if (state.selectedDate) {
      dispatch({ type: "SLOTS_LOADING" });
      fetchSlots(state.selectedDate);
    }
  }, [fetchSlots, state.selectedDate]);

  const selectSlot = useCallback((slot: Slot) => {
    dispatch({ type: "SELECT_SLOT", slot });
  }, []);

  const validateLocation = useCallback((): boolean => {
    const { formData } = state;
    const errors: Partial<Record<keyof BookingFormData, boolean>> = {};

    if (!formData.name?.trim()) errors.name = true;
    if (formData.firstExperience === undefined || formData.firstExperience === null) {
      errors.firstExperience = true;
    }
    if (!formData.email?.trim() || !validateEmail(formData.email) || formData.email.length > 100) {
      errors.email = true;
    }
    if (!formData.phoneNumber?.trim() || formData.phoneNumber.length < 10 || formData.phoneNumber.length > 15) {
      errors.phoneNumber = true;
    }
    if (!formData.location) errors.location = true;
    if (formData.location === "home" && !formData.homeAddress?.trim()) {
      errors.homeAddress = true;
    }
    if (!formData.numberOfFaces) {
      errors.numberOfFaces = true;
    } else if (formData.numberOfFaces === "more") {
      const count = parseInt(customFacesCount || formData.customFacesCount || "0", 10);
      if (!count || count < 6) errors.numberOfFaces = true;
    }

    dispatch({ type: "SET_ERRORS", errors });
    return Object.keys(errors).length === 0;
  }, [state, customFacesCount]);

  const buildBookingFormData = useCallback(
    (slot: Slot): FormData => {
      const { formData } = state;
      const fields: Record<string, string | Blob> = {
        booking_start_date: state.selectedDate,
        booking_end_date: toHalifaxDateString(slot.end),
        booking_start_date_time: toHalifaxWallClock(slot.start),
        booking_end_date_time: toHalifaxWallClock(slot.end),
        glam_spot: formData.location === "studio" ? "studio_session" : "home_service",
        glam_package_id: selectedPackageId,
        number_of_faces: String(facesRef.current),
        first_experience: formData.firstExperience ? "yes" : "no",
        email_address: formData.email ?? "",
        phone_number: formData.phoneNumber ?? "",
        name: formData.name ?? "",
        featured_image_discount: String(formData.featured_image_discount ?? false),
      };
      if (formData.details) fields.additional_info = formData.details;
      if (formData.location === "home") {
        fields.home_address = formData.homeAddress ?? "";
        if (formData.landmark) fields.landmark = formData.landmark;
      }

      const payload = new FormData();
      Object.entries(fields).forEach(([key, value]) => payload.append(key, value));
      if (image) payload.append("client_photo", image);
      return payload;
    },
    [state, selectedPackageId, image]
  );

  const submit = useCallback(async () => {
    const slot = state.selectedSlot;
    if (!slot) {
      dispatch({ type: "SET_ERRORS", errors: { ...state.errors, time: true } });
      toast.error("Please choose an available time.", { position: "top-center" });
      return;
    }

    dispatch({ type: "SUBMIT_START" });
    try {
      await createBooking(buildBookingFormData(slot) as any);
      dispatch({
        type: "SUBMIT_SUCCESS",
        confirmation: { slot, name: state.formData.name ?? "" },
      });
      setImage(null);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error(
          "That time was just taken. We've refreshed the available times — please pick another.",
          { position: "top-center", autoClose: 5000 }
        );
        dispatch({ type: "SLOT_TAKEN", slots: error.response.data?.suggestions ?? [] });
        // Re-sync the full list for the date in the background.
        if (state.selectedDate) fetchSlots(state.selectedDate);
        return;
      }
      dispatch({
        type: "SUBMIT_ERROR",
        message: "We couldn't complete your booking. Your details are saved — please try again.",
      });
      toast.error("We couldn't complete your booking. Please try again.", {
        position: "top-center",
      });
    }
  }, [state.selectedSlot, state.errors, state.formData.name, state.selectedDate, buildBookingFormData, fetchSlots]);

  const next = useCallback(() => {
    if (state.step === "location") {
      if (validateLocation()) dispatch({ type: "SET_STEP", step: "schedule" });
      return;
    }
    if (!state.selectedDate || !state.selectedSlot) {
      dispatch({
        type: "SET_ERRORS",
        errors: {
          ...state.errors,
          date: !state.selectedDate,
          time: !state.selectedSlot,
        },
      });
      toast.error(
        !state.selectedDate ? "Please select a date." : "Please choose an available time.",
        { position: "top-center" }
      );
      return;
    }
    submit();
  }, [state.step, state.selectedDate, state.selectedSlot, state.errors, validateLocation, submit]);

  const back = useCallback(() => {
    if (state.step === "schedule") dispatch({ type: "SET_STEP", step: "location" });
  }, [state.step]);

  return {
    step: state.step,
    formData: state.formData,
    errors: state.errors,
    updateFormData,
    image,
    setImage,
    customFacesCount,
    setCustomFacesCount,
    featuredImageConsent,
    handleConsentChange,
    // Schedule / availability
    selectedDate: state.selectedDate,
    slots: state.slots,
    slotsStatus: state.slotsStatus,
    selectedSlot: state.selectedSlot,
    selectDate,
    selectSlot,
    retrySlots,
    // Flow control
    next,
    back,
    isAtSchedule: state.step === "schedule",
    submitStatus: state.submitStatus,
    submitError: state.submitError,
    isSubmitting: state.submitStatus === "submitting",
    showSuccess: state.showSuccess,
    confirmation: state.confirmation,
  };
}
